import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/auth';
import { hasAnyCapability, homePathForRole, type Capability } from '@/lib/rbac';

/**
 * Route-level role gate. Sits inside AuthGuard (which guarantees an
 * authenticated session); redirects users without the required capability
 * to their role's landing page instead of rendering a blank/403 screen.
 */
export function RequireRole({
  capability,
  children,
}: {
  /** Single capability or a list (any-match) — e.g. /requests serves submitters and reviewers. */
  capability: Capability | readonly Capability[];
  children: React.ReactNode;
}) {
  const { user, isLoading } = useAuth();
  const { t } = useTranslation();

  const capabilities = Array.isArray(capability) ? capability : [capability as Capability];
  const denied = !isLoading && user !== null && !hasAnyCapability(user.role, capabilities);

  useEffect(() => {
    if (denied) toast.error(t('errors.accessDenied'));
  }, [denied, t]);

  if (isLoading || !user) return null;
  if (denied) return <Navigate to={homePathForRole(user.role)} replace />;
  return <>{children}</>;
}
